'use client'

import FiltersMenu from '@modules/serp/filters-menu'
import MobileBottomBar from '@modules/serp/mobile-bottom-bar'
import Sidebar from '@modules/serp/sidebar'

import { Suspense, useEffect, useRef, useState } from 'react'

import dynamic from 'next/dynamic'

import type { ExperimentResponse, JobItem, JobSearch, PaginatedResponse } from '@types'

import useSWR from '@hooks/client/useSWR'
import useAddEventListener from '@hooks/useAddEventListener'
import { serpFiltersOpenedEvent, serpPageViewEvent } from '@lib/analytics-events'
import { readLocalStorageValue } from '@lib/helpers/client'

import List from './jobs-list'

import styles from './serp.module.scss'

const CustomerInsightPopup = dynamic(() => import('./customer-insight-popup'), { ssr: false })

export type JobResultsProps = {
  search: JobSearch
  jobs?: PaginatedResponse<JobItem[]>
  isAuthenticated: boolean
  isBot: boolean
  children: React.ReactNode
}

const Serp = ({ search, jobs, isAuthenticated, isBot, children }: JobResultsProps): JSX.Element => {
  const [isMobileSearchFiltersOpen, setIsMobileSearchFiltersOpened] = useState(false)
  const [isFiltersBarVisible, setIsFiltersBarVisible] = useState<boolean>(false)
  const { data: experiments } = useSWR<ExperimentResponse>(isAuthenticated ? '/jobseeker/experiments' : null)
  const [hasInteractedWithInsightsPopup, setHasInteractedWithInsightsPopup] = useState<boolean>(
    !!readLocalStorageValue('customer-insight-closed'),
  )
  const listRef = useRef<HTMLDivElement>(null)
  const { page } = search

  useAddEventListener('scroll', () => {
    if (listRef.current) {
      setIsFiltersBarVisible(window.scrollY > 300 && listRef.current?.getBoundingClientRect().bottom > 300)
    }
  })

  const onMobileFiltersButtonClick = (): void => {
    serpFiltersOpenedEvent()
    setIsMobileSearchFiltersOpened(true)
  }

  useEffect(() => {
    if (search.job.micro || search.location.id) {
      serpPageViewEvent(search, jobs?.totalElements)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, jobs?.totalElements])

  const isCustomerInsightPopupVisible =
    !isBot &&
    (experiments?.data?.experimentTypes?.includes('SHOW_CUSTOMER_INSIGHT_EXPERIMENT') ?? false) &&
    !hasInteractedWithInsightsPopup

  return (
    <>
      <div className={styles.container}>
        <Sidebar search={search} />
        {jobs && jobs?.totalElements > 0 && (
          <List
            paginatedJobs={jobs}
            onFiltersButtonClick={onMobileFiltersButtonClick}
            ref={listRef}
            isAuthenticated={isAuthenticated}
            paginationInfo={{
              page: page,
              total: jobs?.totalElements ?? 0,
            }}
          />
        )}
        {jobs?.totalElements === 0 && children}
      </div>
      <Suspense>
        <FiltersMenu
          search={search}
          isOpened={isMobileSearchFiltersOpen}
          onClose={(): void => setIsMobileSearchFiltersOpened(false)}
        />
      </Suspense>
      <MobileBottomBar onFiltersButtonClick={onMobileFiltersButtonClick} isVisible={isFiltersBarVisible} />
      {isCustomerInsightPopupVisible && (
        <CustomerInsightPopup
          onClose={(): void => setHasInteractedWithInsightsPopup(true)}
          isOpen={isCustomerInsightPopupVisible}
        />
      )}
    </>
  )
}

export default Serp
